// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { DefiExchangeRateCTO } from '@shared/defiCTO'
import { TimeStep } from '@shared/Enums'
import { FeeAccrualAccountCTO, FeeAccrualCTO } from '@shared/FeeAccrualCTO'
import { logOT } from '@shared/loggingShared'
import { EthAddr } from '@shared/SchemaTypes'
import axios from 'axios'
import { Dayjs } from 'dayjs'
import { User } from 'oidc-client-ts'

export const fetchDefiExchangeRate = async (
  serverApiBaseUrl: string,
  restAPIKey: string,
  poolAddr: EthAddr,
  timestamp?: number
): Promise<DefiExchangeRateCTO> => {
  const url = timestamp
    ? `${serverApiBaseUrl}/defiExchangeRate/${poolAddr}?timestamp=${timestamp}`
    : `${serverApiBaseUrl}/defiExchangeRate/${poolAddr}`
  const r = await axios.get(url, {
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': restAPIKey,
    },
  })
  // Adjust if your API response structure is different
  return r.data.defiExchangeRateCTO
}

export const fetchDefiExchangeRateHistory = async (
  poolAddr: EthAddr,
  timeStep: TimeStep,
  endDateTime: Dayjs,
  serverApiBaseUrl: string,
  restAPIKey: string
): Promise<DefiExchangeRateCTO[]> => {
  const url = `${serverApiBaseUrl}/defiExchangeRateHistory/${poolAddr}?endDateTime=${endDateTime.format('YYYY-MM-DDTHH:mm:ss[Z]')}&timeStep=${timeStep}`
  logOT.info('fetchDefiExchangeRateHistory', url)
  const r = await axios.get(url, {
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': restAPIKey,
    },
  })
  return r.data.defiExchangeRateCTOs
}

export const fetchDefiFeeAccrualHistory = async (
  poolAddr: EthAddr,
  timeStep: TimeStep,
  endDateTime: Dayjs,
  serverApiBaseUrl: string,
  restAPIKey: string
): Promise<FeeAccrualCTO[]> => {
  const url = `${serverApiBaseUrl}/defiFeeAccrualHistory/${poolAddr}?endDateTime=${endDateTime.format('YYYY-MM-DDTHH:mm:ss[Z]')}&timeStep=${timeStep}`
  logOT.info('fetchDefiFeeAccrualHistory', url)
  const r = await axios.get(url, {
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': restAPIKey,
    },
  })

  return r.data.feeAccrualCTOs
}
export const fetchDefiFeeAccrual = async (
  poolAddr: EthAddr,
  serverApiBaseUrl: string,
  restAPIKey: string
): Promise<FeeAccrualCTO> => {
  const url = `${serverApiBaseUrl}/defiFeeAccrual/${poolAddr}`
  logOT.info('fetchDefiFeeAccrual', url)
  const r = await axios.get(url, {
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': restAPIKey,
    },
  })
  logOT.info('fetchDefiFeeAccrual response', r.data)
  logOT.info('fetchDefiFeeAccrual response', r.data.feeAccrualCTO)
  return r.data.feeAccrualCTO
}

export const postDefiFeeAccrualRepayment = async (
  feeAccrualAccountCTO: FeeAccrualAccountCTO,
  serverApiBaseUrl: string,
  restAPIKey: string,
  cognitoUser?: User
): Promise<FeeAccrualCTO> => {
  const accessToken = cognitoUser?.access_token
  const headers = {
    Authorization: `Bearer ${accessToken}`,
    'x-api-key': restAPIKey,
    'Content-Type': 'application/json', // Assuming you're sending JSON data
  }

  const url = `${serverApiBaseUrl}/defiFeeAccrualRepayment/${feeAccrualAccountCTO.poolAddr}`
  logOT.info('defiFeeAccrualRepayment', url, headers)
  const r = await axios.put(url, feeAccrualAccountCTO, { headers })
  const data = r.data

  return data.feeAccrualCTO
}
